<template>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div class="space-y-8">
            <!-- Error State -->
            <div v-if="error" class="p-4 bg-error-50 border border-error-200 rounded-md">
                <p class="text-error-600">{{ error }}</p>
            </div>

            <!-- Game Details -->
            <div v-else-if="gameDetails" class="space-y-8">
                <!-- Header -->
                <div class="flex items-center justify-between">
                    <button @click="goBack" class="text-primary-600 hover:text-primary-800 dark:text-primary-400">
                        ← Back
                    </button>
                    <div class="text-right">
                        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Game Details</h1>
                        <p class="text-gray-600 dark:text-gray-400">{{ formatDate(gameDetails.game.date) }}</p>
                    </div>
                </div>

                <!-- Scoreboard -->
                <div class="card">
                    <div class="card-body">
                        <div class="flex justify-center items-center space-x-8">
                            <!-- Away Team -->
                            <div class="text-center">
                                <div class="text-xl font-bold">{{ gameDetails.game.away_username }}</div>
                                <div class="text-lg text-gray-600 dark:text-gray-400">{{ gameDetails.game.away_team }}</div>
                                <div class="text-sm text-gray-500">Away</div>
                                <div class="text-4xl font-bold text-primary-600 dark:text-primary-400 mt-2">
                                    {{ gameDetails.game.away_score }}
                                </div>
                            </div>

                            <!-- VS -->
                            <div class="text-2xl font-bold text-gray-400">vs</div>

                            <!-- Home Team -->
                            <div class="text-center">
                                <div class="text-xl font-bold">{{ gameDetails.game.home_username }}</div>
                                <div class="text-lg text-gray-600 dark:text-gray-400">{{ gameDetails.game.home_team }}</div>
                                <div class="text-sm text-gray-500">Home</div>
                                <div class="text-4xl font-bold text-primary-600 dark:text-primary-400 mt-2">
                                    {{ gameDetails.game.home_score }}
                                </div>
                            </div>
                        </div>

                        <!-- Winner -->
                        <div v-if="gameDetails.game.winner_username" class="text-center mt-4 space-y-2">
                            <div class="text-lg font-medium text-success-600 dark:text-success-400">
                                🏆 Winner: {{ gameDetails.game.winner_username }}
                            </div>
                            <!-- Shootout Winner Indicator -->
                            <div v-if="isShootoutGame" class="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900 dark:to-pink-900 text-purple-800 dark:text-purple-200 font-medium text-sm border border-purple-200 dark:border-purple-700">
                                <svg class="w-4 h-4 mr-2 text-purple-600 dark:text-purple-300" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M10 2L3 7v11a1 1 0 001 1h12a1 1 0 001-1V7l-7-5zM10 4.236L16 8.464V17h-1v-4a1 1 0 00-1-1h-8a1 1 0 00-1 1v4H4V8.464l6-4.228zM7 17v-3h6v3H7z" clip-rule="evenodd" />
                                </svg>
                                SHOOTOUT VICTORY
                            </div>
                            <!-- OT Winner Indicator (for non-shootout OT) -->
                            <div v-else-if="isOvertimeGame" class="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-amber-100 to-yellow-100 dark:from-amber-900 dark:to-yellow-900 text-amber-800 dark:text-amber-200 font-medium text-sm border border-amber-200 dark:border-amber-700">
                                <svg class="w-4 h-4 mr-2 text-amber-600 dark:text-amber-300" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clip-rule="evenodd" />
                                </svg>
                                ⚡ OVERTIME VICTORY ⚡
                            </div>
                        </div>
                        <div v-else class="text-center mt-4">
                            <div class="text-lg font-medium text-gray-600 dark:text-gray-400">
                                🤝 Tie Game
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Shootout Indicator Card -->
                <div v-if="isShootoutGame" class="card">
                    <div class="card-body">
                        <div class="flex items-center justify-center space-x-4 py-4">
                            <svg class="w-8 h-8 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M10 2L3 7v11a1 1 0 001 1h12a1 1 0 001-1V7l-7-5zM10 4.236L16 8.464V17h-1v-4a1 1 0 00-1-1h-8a1 1 0 00-1 1v4H4V8.464l6-4.228zM7 17v-3h6v3H7z" clip-rule="evenodd" />
                            </svg>
                            <div class="text-center">
                                <div class="text-lg font-semibold text-purple-600 dark:text-purple-400">Game decided by Shootout</div>
                                <div class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                    After a scoreless overtime period, {{ gameDetails.game.winner_username }} won in the shootout
                                </div>
                            </div>
                            <svg class="w-8 h-8 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M10 2L3 7v11a1 1 0 001 1h12a1 1 0 001-1V7l-7-5zM10 4.236L16 8.464V17h-1v-4a1 1 0 00-1-1h-8a1 1 0 00-1 1v4H4V8.464l6-4.228zM7 17v-3h6v3H7z" clip-rule="evenodd" />
                            </svg>
                        </div>
                    </div>
                </div>
                
                <!-- Commentary -->
                <div v-if="gameDetails.commentary && gameDetails.commentary.length > 0" class="card">
                    <div class="card-header">
                        <h3 class="text-lg font-medium">Game Insights</h3>
                    </div>
                    <div class="card-body">
                        <ul class="space-y-2">
                            <li v-for="comment in gameDetails.commentary" :key="comment" class="text-gray-700 dark:text-gray-300">
                                • {{ comment }}
                            </li>
                        </ul>
                    </div>
                </div>

                <!-- Game Stats -->
                <div class="space-y-6">
                    <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Game Breakdown</h2>
                    
                    <div :class="[
                        'card',
                        selectedTab === 'overtime' && !isShootoutGame ? 'ring-2 ring-amber-200 dark:ring-amber-800 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950 dark:to-yellow-950' : ''
                    ]">
                        <!-- Tab Navigation -->
                        <div :class="[
                            'card-header border-b-0',
                            selectedTab === 'overtime' && !isShootoutGame ? 'bg-gradient-to-r from-amber-100 to-yellow-100 dark:from-amber-900 dark:to-yellow-900 border-amber-200 dark:border-amber-700' : ''
                        ]">
                            <div class="flex flex-wrap gap-1 sm:gap-2">
                                <button
                                    @click="selectedTab = 'final'"
                                    :class="[
                                        'px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200',
                                        selectedTab === 'final'
                                            ? 'bg-primary-600 text-white shadow-sm'
                                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                                    ]"
                                >
                                    Final
                                </button>
                                <button
                                    v-for="period in periods"
                                    :key="period"
                                    @click="selectedTab = period === 4 ? 'overtime' : `period${period}`"
                                    :class="[
                                        'px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 flex items-center gap-1',
                                        (period === 4 && selectedTab === 'overtime') || selectedTab === `period${period}`
                                            ? 'bg-primary-600 text-white shadow-sm'
                                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                                    ]"
                                >
                                    {{ period === 4 ? 'Overtime' : `Period ${period}` }}
                                    <div v-if="period === 4 && !isShootoutGame && selectedTab === 'overtime'" class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-200 animate-pulse ml-1">
                                        <svg class="w-2.5 h-2.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clip-rule="evenodd" />
                                        </svg>
                                        SUDDEN DEATH
                                    </div>
                                </button>
                            </div>
                        </div>
                        
                        <!-- Tab Content -->
                        <div class="card-body">
                            <!-- Final Stats -->
                            <div v-if="selectedTab === 'final'" class="space-y-6">
                                <div v-for="username in usernames" :key="`final-${username}`">
                                    <h4 class="font-medium text-gray-900 dark:text-white mb-3">{{ username }}</h4>
                                    <div v-if="getFinalStats(username)" class="grid grid-cols-4 sm:grid-cols-8 gap-2 sm:gap-4 text-sm">
                                        <div class="stat-card">
                                            <div class="stat-value text-sm sm:text-base">{{ getFinalStats(username)?.goals || 0 }}</div>
                                            <div class="stat-label text-xs">Goals</div>
                                        </div>
                                        <div class="stat-card">
                                            <div class="stat-value text-sm sm:text-base">{{ getFinalStats(username)?.shots || 0 }}</div>
                                            <div class="stat-label text-xs">Shots</div>
                                        </div>
                                        <div class="stat-card">
                                            <div class="stat-value text-sm sm:text-base">{{ getFinalStats(username)?.hits || 0 }}</div>
                                            <div class="stat-label text-xs">Hits</div>
                                        </div>
                                        <div class="stat-card">
                                            <div class="stat-value text-sm sm:text-base">{{ formatTime(getFinalStats(username)?.time_on_attack) }}</div>
                                            <div class="stat-label text-xs">Time on Attack</div>
                                        </div>
                                        <div class="stat-card">
                                            <div class="stat-value text-sm sm:text-base">{{ getFinalStats(username)?.passing_pct || 0 }}%</div>
                                            <div class="stat-label text-xs">Passing %</div>
                                        </div>
                                        <div class="stat-card">
                                            <div class="stat-value text-sm sm:text-base">{{ getFinalStats(username)?.faceoff_wins || 0 }}</div>
                                            <div class="stat-label text-xs">Faceoffs</div>
                                        </div>
                                        <div class="stat-card">
                                            <div class="stat-value text-sm sm:text-base">{{ getFinalStats(username)?.powerplay_goals || 0 }}/{{ getFinalStats(username)?.powerplay_opportunities || 0 }}</div>
                                            <div class="stat-label text-xs">Power Play</div>
                                        </div>
                                        <div class="stat-card">
                                            <div class="stat-value text-sm sm:text-base">{{ getFinalStats(username)?.powerplay_percentage || '0%' }}</div>
                                            <div class="stat-label text-xs">PP%</div>
                                        </div>
                                    </div>
                                    <div v-else class="text-gray-500 dark:text-gray-400 text-sm">
                                        No stats available
                                    </div>
                                </div>
                            </div>

                            <!-- Period-specific Stats -->
                            <div v-else class="space-y-6">
                                <div v-for="username in usernames" :key="`${selectedTab}-${username}`">
                                    <h4 class="font-medium text-gray-900 dark:text-white mb-3">{{ username }}</h4>
                                    <div v-if="getPeriodStats(username, selectedTab)" class="grid grid-cols-4 sm:grid-cols-8 gap-2 sm:gap-4 text-sm">
                                        <div :class="[
                                          'stat-card',
                                          selectedTab === 'overtime' && !isShootoutGame ? 'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950 dark:to-yellow-950 border-amber-200 dark:border-amber-800' : ''
                                        ]">
                                            <div :class="[
                                              'stat-value text-sm sm:text-base',
                                              selectedTab === 'overtime' && !isShootoutGame && (getPeriodStats(username, selectedTab)?.goals || 0) > 0 ? 'text-amber-600 dark:text-amber-400 font-bold animate-pulse' : ''
                                            ]">{{ getPeriodStats(username, selectedTab)?.goals || 0 }}</div>
                                            <div class="stat-label text-xs">Goals</div>
                                        </div>
                                        <div :class="[
                                          'stat-card',
                                          selectedTab === 'overtime' && !isShootoutGame ? 'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950 dark:to-yellow-950 border-amber-200 dark:border-amber-800' : ''
                                        ]">
                                            <div class="stat-value text-sm sm:text-base">{{ getPeriodStats(username, selectedTab)?.shots || 0 }}</div>
                                            <div class="stat-label text-xs">Shots</div>
                                        </div>
                                        <div :class="[
                                          'stat-card',
                                          selectedTab === 'overtime' && !isShootoutGame ? 'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950 dark:to-yellow-950 border-amber-200 dark:border-amber-800' : ''
                                        ]">
                                            <div class="stat-value text-sm sm:text-base">{{ getPeriodStats(username, selectedTab)?.hits || 0 }}</div>
                                            <div class="stat-label text-xs">Hits</div>
                                        </div>
                                        <div :class="[
                                          'stat-card',
                                          selectedTab === 'overtime' && !isShootoutGame ? 'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950 dark:to-yellow-950 border-amber-200 dark:border-amber-800' : ''
                                        ]">
                                            <div class="stat-value text-sm sm:text-base">{{ formatTime(getPeriodStats(username, selectedTab)?.time_on_attack) }}</div>
                                            <div class="stat-label text-xs">Time on Attack</div>
                                        </div>
                                        <div :class="[
                                          'stat-card',
                                          selectedTab === 'overtime' && !isShootoutGame ? 'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950 dark:to-yellow-950 border-amber-200 dark:border-amber-800' : ''
                                        ]">
                                            <div class="stat-value text-sm sm:text-base">{{ getPeriodStats(username, selectedTab)?.passing_pct || 0 }}%</div>
                                            <div class="stat-label text-xs">Passing %</div>
                                        </div>
                                        <div :class="[
                                          'stat-card',
                                          selectedTab === 'overtime' && !isShootoutGame ? 'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950 dark:to-yellow-950 border-amber-200 dark:border-amber-800' : ''
                                        ]">
                                            <div class="stat-value text-sm sm:text-base">{{ getPeriodStats(username, selectedTab)?.faceoff_wins || 0 }}</div>
                                            <div class="stat-label text-xs">Faceoffs</div>
                                        </div>
                                        <div :class="[
                                          'stat-card',
                                          selectedTab === 'overtime' && !isShootoutGame ? 'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950 dark:to-yellow-950 border-amber-200 dark:border-amber-800' : ''
                                        ]">
                                            <div :class="[
                                              'stat-value text-sm sm:text-base',
                                              selectedTab === 'overtime' && !isShootoutGame && (getPeriodStats(username, selectedTab)?.powerplay_goals || 0) > 0 ? 'text-amber-600 dark:text-amber-400 font-bold' : ''
                                            ]">{{ getPeriodStats(username, selectedTab)?.powerplay_goals || 0 }}/{{ getPeriodStats(username, selectedTab)?.powerplay_opportunities || 0 }}</div>
                                            <div class="stat-label text-xs">Power Play</div>
                                        </div>
                                        <div :class="[
                                          'stat-card',
                                          selectedTab === 'overtime' && !isShootoutGame ? 'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950 dark:to-yellow-950 border-amber-200 dark:border-amber-800' : ''
                                        ]">
                                            <div class="stat-value text-sm sm:text-base">{{ getPeriodStats(username, selectedTab)?.powerplay_percentage || '0' }}</div>
                                            <div class="stat-label text-xs">PP%</div>
                                        </div>
                                    </div>
                                    <div v-else class="text-gray-500 dark:text-gray-400 text-sm">
                                        No stats for this period
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { gameApi } from '@/services/api'

interface PeriodStat {
    goals: number
    shots: number
    hits: number
    time_on_attack: any
    passing_pct: number
    faceoff_wins: number
    powerplay_goals: number
    powerplay_opportunities: number
    powerplay_percentage: string
}

interface GameDetails {
    game: {
        id: number
        date: string
        created_at: string
        home_username: string
        away_username: string
        winner_username?: string
        home_team: string
        away_team: string
        home_score: number
        away_score: number
        is_overtime?: boolean
        is_shootout?: boolean
    }
    periodStats: Record<string, Record<number, PeriodStat>>
    commentary: string[]
}

const route = useRoute()
const router = useRouter()
const error = ref<string | null>(null)
const gameDetails = ref<GameDetails | null>(null)
const selectedTab = ref('final')

const gameId = computed(() => parseInt(route.params.id as string))

const usernames = computed(() => {
    if (!gameDetails.value) return []
    const playerNames = Object.keys(gameDetails.value.periodStats)
    const preferredOrder = ['Aendy', 'Tobbba_']
    
    // Filter and order players based on preferred order, then add any others
    const orderedPlayers = []
    
    // Add players in preferred order if they exist
    for (const name of preferredOrder) {
        if (playerNames.includes(name)) {
            orderedPlayers.push(name)
        }
    }
    
    // Add any remaining players not in the preferred order
    for (const name of playerNames) {
        if (!preferredOrder.includes(name)) {
            orderedPlayers.push(name)
        }
    }
    
    return orderedPlayers
})

const periods = computed(() => {
    if (!gameDetails.value) return []
    const allPeriods = new Set<number>()
    Object.values(gameDetails.value.periodStats).forEach(playerStats => {
        Object.keys(playerStats).forEach(period => {
            allPeriods.add(parseInt(period))
        })
    })
    return Array.from(allPeriods).sort((a, b) => a - b)
})

const isOvertimeGame = computed(() => {
    return gameDetails.value?.game.is_overtime || periods.value.includes(4)
})

const isShootoutGame = computed(() => {
    return gameDetails.value?.game.is_shootout || false
})

const fetchGameDetails = async () => {
    try {
        error.value = null

        const response = await gameApi.getGameDetails(gameId.value)
        gameDetails.value = response.data
        
    } catch (err: any) {
        error.value = err.response?.data?.error || 'Failed to load game details'
    }
}

const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC'
    })
}

const formatTime = (timeObj: any) => {
    if (!timeObj) return '0:00'
    const minutes = timeObj.minutes || 0
    const seconds = timeObj.seconds || 0
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

const getPeriodStats = (username: string, tab: string) => {
    if (!gameDetails.value?.periodStats[username]) return null
    
    if (tab === 'overtime') {
        return gameDetails.value.periodStats[username][4] || null
    }
    
    const periodNum = parseInt(tab.replace('period', ''))
    return gameDetails.value.periodStats[username][periodNum] || null
}

const getFinalStats = (username: string) => {
    if (!gameDetails.value?.periodStats[username]) return null
    
    const playerStats = gameDetails.value.periodStats[username]
    const finalStats = {
        goals: 0,
        shots: 0,
        hits: 0,
        time_on_attack: { minutes: 0, seconds: 0 },
        passing_pct: 0,
        faceoff_wins: 0,
        powerplay_goals: 0,
        powerplay_opportunities: 0,
        powerplay_percentage: '0%'
    }
    
    // Calculate passing percentage using your specified formula
    let period1 = parseFloat(String(playerStats[1]?.passing_pct || 0))
    let period2 = parseFloat(String(playerStats[2]?.passing_pct || 0))
    let period3 = parseFloat(String(playerStats[3]?.passing_pct || 0))
    let period4 = parseFloat(String(playerStats[4]?.passing_pct || 0))
    let isPeriod4 = playerStats[4] != null
    
    finalStats.passing_pct = parseFloat(((period1 + period2 + period3 + (isPeriod4 ? period4 : 0)) / (isPeriod4 ? 4 : 3)).toFixed(1))
    
    Object.values(playerStats).forEach((periodStat: PeriodStat) => {
        finalStats.goals += periodStat.goals
        finalStats.shots += periodStat.shots
        finalStats.hits += periodStat.hits
        finalStats.faceoff_wins += periodStat.faceoff_wins
        finalStats.powerplay_goals += periodStat.powerplay_goals
        finalStats.powerplay_opportunities += periodStat.powerplay_opportunities
        
        if (periodStat.time_on_attack) {
            finalStats.time_on_attack.minutes += periodStat.time_on_attack.minutes || 0
            finalStats.time_on_attack.seconds += periodStat.time_on_attack.seconds || 0
        }
    })
    
    if (finalStats.time_on_attack.seconds >= 60) {
        finalStats.time_on_attack.minutes += Math.floor(finalStats.time_on_attack.seconds / 60)
        finalStats.time_on_attack.seconds = finalStats.time_on_attack.seconds % 60
    }
    
    finalStats.powerplay_percentage = finalStats.powerplay_opportunities > 0 
        ? `${Math.round((finalStats.powerplay_goals / finalStats.powerplay_opportunities) * 100)}%`
        : '0%'
    
    return finalStats
}

const goBack = () => {
    if (window.history.length > 1) {
        router.go(-1)
    } else {
        router.push('/')
    }
}

onMounted(() => {
    fetchGameDetails()
})
</script>